import { InjectedConnector } from '@web3-react/injected-connector'
import { getProvider } from '@binance/w3w-ethereum-provider'
// import { SUPPORTED_CHAIN_IDS } from 'connectors'
import { ConnectorUpdate } from '@web3-react/types'
import { ChainId } from '@xterswap/sdk-core'

// Define wallet type enum
export enum WalletSDKType {
  METAMASK = 'METAMASK',
  WALLETCONNECT = 'WALLET CONNECT',
  TRUST = 'TRUST',
  SAFEPAL = 'SAFEPAL',
  OKX = 'OKX',
  BINANCE = 'BINANCE',
  BYBIT = 'BYBIT',
  GATE = 'GATE',
}

declare global {
  interface Window {
    okxwallet?: any
    gatewallet?: any
    bybitWallet?: any
    metamaskWallet?: any
    hijackData?: any
  }
}

const SUPPORTED_CHAIN_IDS = [ChainId.XTERIO_TESTNET, ChainId.XTERIO, ChainId.BNB]

// Add wallet download URL configuration
export const WALLET_DOWNLOAD_URLS: Partial<Record<WalletSDKType, string>> = {
  [WalletSDKType.OKX]: 'https://chromewebstore.google.com/detail/okx-wallet/mcohilncbfahbmgdjkbpemcciiolgcge',
  [WalletSDKType.GATE]: 'https://chromewebstore.google.com/detail/gate-wallet/cpmkedoipcpimgecpmgpldfpohjplkpp',
  [WalletSDKType.BYBIT]: 'https://chromewebstore.google.com/detail/bybit-wallet/pdliaogehgdbhbnmkklieghmmjkpigpa',
} as const

export class CustomInjectedConnector extends InjectedConnector {
  private readonly providerGetter: () => any
  private readonly walletType: WalletSDKType

  constructor(providerGetter: () => any, walletType: WalletSDKType, supportedChainIds: number[]) {
    // Must call the parent class constructor
    super({ supportedChainIds })

    this.providerGetter = providerGetter
    this.walletType = walletType
  }

  // Add a public getter for walletType
  public getWalletType(): WalletSDKType {
    return this.walletType
  }

  // Key: Explicitly override activate
  async activate(): Promise<ConnectorUpdate> {
    console.log(`[Custom] Activating ${this.walletType} with delay`)

    try {
      // 1. Get custom Provider
      const provider = await this.getProvider()
      if (!provider) throw new Error('No provider available')

      // 2. Manually request account access
      const accounts = await provider
        .request({
          method: 'eth_requestAccounts',
        })
        .catch((error: Error) => {
          console.error('Account request failed:', error)
          throw new Error('User denied account access')
        })

      // 3. Get current chain ID
      const chainId = await provider.request({
        method: 'eth_chainId',
      })

      // 4. Add event listeners for network and account changes
      if (provider.on) {
        provider.on('chainChanged', this.customHandleChainChanged)
        provider.on('accountsChanged', this.customHandleAccountsChanged)
        provider.on('close', this.customHandleClose)
        provider.on('networkChanged', this.customHandleNetworkChanged)
      }

      // 5. Return update data
      return {
        provider,
        account: accounts[0],
        chainId: Number(chainId),
      }
    } catch (error) {
      console.error('Activation failed:', error)
      throw error
    }
  }

  // Handle network change
  private customHandleChainChanged = async (chainId: string) => {
    console.log('Network changed to:', chainId)
    try {
      const provider = await this.getProvider()
      this.emitUpdate({
        chainId: Number(chainId),
        provider: provider,
      })
    } catch (error) {
      console.error('Failed to re-activate after network change:', error)
    }
  }

  // Handle account change
  private customHandleAccountsChanged = async (accounts: string[]) => {
    console.log('Accounts changed:', accounts)
    if (accounts.length === 0) {
      this.emitDeactivate()
      console.warn('No accounts available after account change')
    } else {
      this.emitUpdate({
        account: accounts[0],
      })
    }
  }

  private customHandleClose = (code: string, reason: string) => {
    console.log('Handling close event with payload', code, reason)
    this.emitDeactivate()
  }

  private customHandleNetworkChanged = async (networkId: string) => {
    console.log('Handling networkChanged event with payload', networkId)
    const provider = await this.getProvider()
    this.emitUpdate({
      chainId: networkId,
      provider: provider,
    })
  }

  // Key: Correctly override prototype method
  async getProvider(): Promise<any> {
    console.log('=== CUSTOM GETPROVIDER TRIGGERED ===')
    console.log('[Custom] getProvider called for:', this.walletType)

    // Execute custom logic
    const provider = await this.providerGetter()

    if (!provider) {
      const downloadUrl = WALLET_DOWNLOAD_URLS[this.walletType]
      if (downloadUrl) {
        console.warn(`Wallet ${this.walletType} not installed`)
        window.open(downloadUrl, '_blank')
        throw new Error(`Please install ${this.walletType} wallet`)
      }
    }

    return provider
  }

  public async deactivate(): Promise<void> {
    console.log(`[Custom] Deactivating ${this.walletType}`)

    try {
      const provider = await this.getProvider()
      if (provider && provider.removeListener) {
        provider.removeListener('chainChanged', this.customHandleChainChanged)
        provider.removeListener('accountsChanged', this.customHandleAccountsChanged)
        provider.removeListener('close', this.customHandleClose)
        provider.removeListener('networkChanged', this.customHandleNetworkChanged)
      }

      console.log(`[Custom] Successfully deactivated ${this.walletType}`)
    } catch (error) {
      console.error('Deactivation failed:', error)
      throw error
    }
  }
}

export const createWalletConnector = (providerGetter: () => any, walletType: WalletSDKType) => {
  const connector = new CustomInjectedConnector(providerGetter, walletType, SUPPORTED_CHAIN_IDS)

  // Force bind methods to the current instance
  connector.activate = connector.activate.bind(connector)
  connector.getProvider = connector.getProvider.bind(connector)

  return connector
}

const findMetaMaskProvider = () => {
  const providers =
    (window.hijackData?.providerList && window.hijackData.providerList.map((i: any) => i.provider)) ||
    (window as any).ethereum?.providers ||
    []
  let selectedProvider: any
  for (const provider of providers || []) {
    if (provider.isMetaMask && !provider.isOkxWallet) {
      selectedProvider = provider
      break
    }
  }
  const windowEthereum = window.metamaskWallet || window.ethereum
  return selectedProvider ?? (windowEthereum?.isMetaMask && windowEthereum._metamask ? windowEthereum : null)
}

// Provider retrieval functions for each wallet
export const getProviders = {
  okx: async () => {
    if (typeof window.okxwallet === 'undefined') return

    // Special initialization for OKX
    if (window.okxwallet.providers) {
      const okxProvider = window.okxwallet.providers.find((p: any) => p.isOkxWallet)
      await okxProvider?.enable()
      return okxProvider
    }

    return window.okxwallet
  },
  bybit: () => window?.bybitWallet,
  binance: () => {
    return getProvider({
      chainId: 56,
      rpc: {
        1: process.env.NEXT_PUBLIC_RPC_ETHEREUM || 'https://ethereum-rpc.publicnode.com',
        56: process.env.NEXT_PUBLIC_RPC_BSC || 'https://bsc-dataseed.bnbchain.org',
        204: process.env.NEXT_PUBLIC_RPC_OPBNB || 'https://opbnb-mainnet-rpc.bnbchain.org',
        42161: process.env.NEXT_PUBLIC_RPC_ARBITRUM || 'https://arbitrum-one.publicnode.com',
        137: process.env.NEXT_PUBLIC_RPC_POLYGON || 'https://polygon-bor.publicnode.com',
        112358: process.env.NEXT_PUBLIC_RPC_XTERIO || 'https://bnb-rpc.xter.io',
        2702128: process.env.NEXT_PUBLIC_RPC_XTERIO_ETH || 'https://eth-rpc.xter.io',
        8453: process.env.NEXT_PUBLIC_RPC_BASE || 'https://mainnet.base.org',
        5: process.env.NEXT_PUBLIC_RPC_GOERLI || 'https://eth-goerli.public.blastapi.io',
        11155111: process.env.NEXT_PUBLIC_RPC_SEPOLIA || 'https://ethereum-sepolia-rpc.publicnode.com',
        97: process.env.NEXT_PUBLIC_RPC_BSC_TESTNET || 'https://data-seed-prebsc-2-s1.binance.org:8545',
        5611: process.env.NEXT_PUBLIC_RPC_OPBNB_TESTNET || 'https://opbnb-testnet-rpc.bnbchain.org',
        1637450: process.env.NEXT_PUBLIC_RPC_XTERIO_TESTNET || 'https://testnet-rpc.xter.io',
      },
    })
  },
  gate: () => window?.gatewallet,
  metamask: () => findMetaMaskProvider(),
}

// Export connectors for each wallet
export const walletConnectors = {
  [WalletSDKType.OKX]: createWalletConnector(getProviders.okx, WalletSDKType.OKX),
  [WalletSDKType.BINANCE]: createWalletConnector(getProviders.binance, WalletSDKType.BINANCE),
  [WalletSDKType.GATE]: createWalletConnector(getProviders.gate, WalletSDKType.GATE),
  [WalletSDKType.BYBIT]: createWalletConnector(getProviders.bybit, WalletSDKType.BYBIT),
  [WalletSDKType.METAMASK]: createWalletConnector(getProviders.metamask, WalletSDKType.METAMASK),
}
